import { Radio, RadioGroup, Spinner } from "@blueprintjs/core";
import { FGTextInput, FieldGroup, showError, showSuccess, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useLocation } from "react-router";
import styled from "styled-components";

import {
  CheckoutItemDto,
  EPlatform,
  ERequestState,
  QuickUpdateRequestStatusIdCommand,
  RequestApi,
  RequestDto,
  StripeApi,
} from "../../api";
import { FGEuroMaskInput } from "../../components/formGenerator/FGEuroMaskInput";
import { useGlobalData } from "../../contexts";
import { useApiService, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { IngenicoPaiment, PaimentData } from "../common";
import { EDrupalRoutes } from "../EDrupalRoutes";
import { DrupalFormGenerator } from "./DrupalFormGenerator";
import { DrupalSharepointFilesList } from "./DrupalSharepointFilesList";
import { DrupalStepperButtonBloc } from "./DrupalStepperButtonBloc";
import { DrupalFieldSet } from "./DurpalFieldSet";
import { RequestInformationsDisplay } from "./RequestInformationsDisplay";
import { isBrcs } from "../DrupalApp";

export interface IDrupalRequestResumeProps {
  requestForm: JSX.Element;
  requestDto: RequestDto;
  bill: any;
  onStatusUpdated?: () => void;
}

const StyledForm = styled.div`
  margin-left: 4em;
`;

const StyledRadioGroup = styled.div`
  margin: 2em;
  text-align: left !important;
  display: flex;
`;

const Container = styled.div`
  width: 50%;
  margin: 3em auto;
  font-size: 20px;
  border: 2px solid #a1a1a1;
  padding: 2rem;
  text-align: center;
  border-radius: 10px;
`;

type paimentMethods = "cashPayment" | "ogonePayment";

export const DrupalRequestResume: React.FunctionComponent<IDrupalRequestResumeProps> = ({
  requestForm,
  requestDto,
  bill,
  onStatusUpdated,
}) => {
  const location = useLocation();

  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const history = useHistory();

  const requestApi = useApiService(RequestApi);
  const stripeApi = useApiService(StripeApi);

  const [ingenicoLoading, setingenicoLoading] = React.useState<boolean>();
  const [payLoading, setpayLoading] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState<paimentMethods>("ogonePayment");
  const [paymentData, setPaymentData] = React.useState<PaimentData>();

  const [paymentInfo, piLoading] = useApiEffect(async () => {
    var res = await requestApi.getRequestPayementInfo(requestDto.requestId);
    res["formattedPrice"] = res.totalPrice.toString().replace(".", ",");
    return res;
  }, [requestDto, currentLanguage]);

  const [shpFiles, shpFilesLoading] = useApiEffect(
    () => (requestDto ? requestApi.getSharepointDoc(requestDto?.requestId) : undefined),
    [requestDto?.requestId]
  );

  const [requestMenu, rmLoading] = useApiEffect(
    () => (requestDto ? requestApi.getRequestMenu(requestDto?.requestId, currentLanguage.languageId) : undefined),
    [requestDto?.requestId]
  );

  const onChangeHandler = React.useCallback((event) => {
    setPaymentMethod(event.target.value);
  }, []);

  const cashPayment = React.useCallback(async () => {
    try {
      const paymentStatusId = await requestApi.getStatusId(ERequestState.PAYMENT_PROCESS);
      await requestApi.updateRequestStatus(requestDto.requestId, paymentStatusId, EPlatform.PORTAL);
      showSuccess(t(ETLCodes.SaveSuccess));
      history.push(EDrupalRoutes.requestList);
    } catch {
      showError(t(ETLCodes.Error));
    }
  }, [history, requestApi, requestDto, t]);

  const downloadFile = React.useCallback(
    async (fileName: string) => {
      return await requestApi.downloadRequestDocument(requestDto?.sharepointUri, fileName);
    },
    [requestApi, requestDto.sharepointUri]
  );

  const getInformationsForOnlinePayment = React.useCallback(async () => {
    const invoiceInfo = await requestApi.getRequestInvoice(requestDto.requestId);

    const structuredComm = invoiceInfo.structuredCommunication?.replace(new RegExp("/", "g"), "");
    let paymentData = {
      amount: invoiceInfo.totalPriceCents,
      cn: "duplicata",
      structuredCommunicationFormated: structuredComm,
      structuredCommunicationRaw: invoiceInfo.structuredCommunication,
    };
    setPaymentData(paymentData);

    return paymentData;
  }, [requestApi, requestDto]);

  const getIgenicoReturnData = React.useCallback(
    async (data) => {
      setingenicoLoading(true);
      if (!data) {
        return;
      }
      await requestApi.quickUpdateRequestStatus(
        new QuickUpdateRequestStatusIdCommand({
          requestId: requestDto.requestId,
          requestStatus: ERequestState.PAYMENT_PROCESS,
          plaform: EPlatform.PORTAL,
          isAutomatic: true,
          languageId: currentLanguage?.languageId,
        })
      );

      if (onStatusUpdated !== null && onStatusUpdated !== undefined) {
        onStatusUpdated();
      }
      var res = location.pathname;
      setingenicoLoading(false);
      history.push(res);
      window.location.reload();
    },
    [history, location.pathname, onStatusUpdated, requestApi, currentLanguage, requestDto.requestId]
  );

  const onNextButtonClick = React.useCallback(async () => {
    switch (paymentMethod) {
      case "cashPayment": {
        setpayLoading(true);
        await cashPayment();
        setpayLoading(false);
        break;
      }
      case "ogonePayment": {
        // All online payments (SRSH and BRCS) now go through Stripe. Ingenico is kept in the
        // codebase but no longer triggered (see IngenicoPaiment below, triggerPaiment={false}).
        setpayLoading(true);
        try {
          let paymentData = await getInformationsForOnlinePayment();
          // BRCS keeps the backend default redirect (BrcsUrl). SRSH returns to its own portal
          // request list, derived from the current location (hash routing).
          const successUrl = isBrcs
            ? undefined
            : `${window.location.origin}${window.location.pathname}#${EDrupalRoutes.requestList}`;
          let stripeSessionLink = await stripeApi.getStripeSessionLink(
            new CheckoutItemDto({
              name: paymentData.cn,
              price: paymentData.amount,
              quantity: 1,
              invoiceStructuredCommunication: paymentData.structuredCommunicationRaw,
              previousLocation: window.location.href,
              successUrl,
            })
          );
          window.location.href = stripeSessionLink.sessionUrl;
        } catch {
          showError(t(ETLCodes.Error));
          setpayLoading(false);
        }
        break;
      }
    }
  }, [cashPayment, paymentMethod, getInformationsForOnlinePayment]);

  return rmLoading || ingenicoLoading ? (
    <Spinner />
  ) : (
    <>
      <RequestInformationsDisplay
        requestNumber={requestDto?.requestNumber}
        requestDate={requestDto?.requestDate}
        requestType={requestMenu?.requestType}
        requestStatus={requestMenu?.requestStatus}
        requestPrice={null}
        bill={bill}
      />

      {requestForm}
      <div style={{ marginBottom: "0.5rem" }}>
        <DrupalSharepointFilesList files={shpFiles} loading={shpFilesLoading} downloadFunc={downloadFile} />
      </div>
      {requestDto?.status === ERequestState.BILLED || requestDto?.status === ERequestState.PAYMENT_REFUSED ? (
        <>
          {/* Payment method radio group */}
          <DrupalFieldSet title={t(ETLCodes.PaymentInformations)}>
            <StyledRadioGroup>
              <RadioGroup label={t(ETLCodes.PaymentMethod)} onChange={onChangeHandler} selectedValue={paymentMethod}>
                <Radio label={t(ETLCodes.OgonePayment)} value={"ogonePayment"} />
                <Radio label={t(ETLCodes.CashPayment)} value={"cashPayment"} />
              </RadioGroup>
            </StyledRadioGroup>

            {/* Payment information */}

            <>
              <StyledForm>
                <DrupalFormGenerator
                  initialValues={paymentInfo}
                  loading={piLoading}
                  editMode={
                    requestDto?.status === ERequestState.BILLED || requestDto?.status === ERequestState.PAYMENT_REFUSED
                  }
                  onSubmit={() => cashPayment}
                  saving={false}
                  hideButtons
                >
                  <FieldGroup columns={2}>
                    <FGEuroMaskInput name={"formattedPrice"} label={t(ETLCodes.PriceVatIncluded)} readonly />
                  </FieldGroup>
                  {paymentMethod === "cashPayment" && (
                    <FieldGroup>
                      <FGTextInput name="srshIban" label={t(ETLCodes.IBAN)} readonly />
                      <FGTextInput
                        name="structuredCommunication"
                        label={t(ETLCodes.StructuredCommunication)}
                        readonly
                      />
                      <hr />
                      {t(ETLCodes.PaymentReceiptMessage)}
                    </FieldGroup>
                  )}
                </DrupalFormGenerator>
              </StyledForm>
            </>
            <DrupalStepperButtonBloc
              backOnly={false}
              disabled={
                requestDto?.status !== ERequestState.BILLED && requestDto?.status !== ERequestState.PAYMENT_REFUSED
              }
              stepNumber={2}
              nextButtonText={t(ETLCodes.General_Pay)}
              nextButtonIcon="credit-card"
              onNextFn={onNextButtonClick}
              loading={payLoading}
              onStepChange={() => {}}
              formik={null}
            />
          </DrupalFieldSet>
        </>
      ) : requestDto?.status === ERequestState.REQUESTED ? (
        <Container>{t(ETLCodes.AwaitingSRSHValidation)}</Container>
      ) : (
        <></>
      )}
      {/* Ingenico kept temporarily but no longer triggered: all payments now go through Stripe. */}
      {!isBrcs && (
        <IngenicoPaiment data={paymentData} triggerPaiment={false} returnParams={getIgenicoReturnData} />
      )}
    </>
  );
};
